import { LitElement, html, css } from 'lit';
import { customElement, state } from 'lit/decorators.js';
import { authStore } from './store/auth-store';
import { RobotInfo } from './types';
import './views/protocol-adapter-view';
import './views/standard-model-view';
import './views/http-target-system-view';
import './views/http-route-rule-view';
import './views/http-route-log-view';
import './views/http-route-debug-view';

type AppRoute = '/' | '/adapter' | '/standard-model' | '/http-target-system' | '/http-route-rule' | '/http-route-log' | '/http-route-debug';

function readAppRoute(): AppRoute {
  const p = window.location.pathname.replace(/\/$/, '') || '/';
  if (p === '/adapter') return '/adapter';
  if (p === '/standard-model') return '/standard-model';
  if (p === '/http-target-system') return '/http-target-system';
  if (p === '/http-route-rule') return '/http-route-rule';
  if (p === '/http-route-log') return '/http-route-log';
  if (p === '/http-route-debug') return '/http-route-debug';
  return '/';
}

@customElement('app-root')
export class AppRoot extends LitElement {
  // 强制使用 css 模板字面量
  static styles = css`
    :host {
      display: block;
      min-height: 100vh;
      color: #333;
    }

    header {
      background-color: #0f172a;
      color: #ffffff;
      padding: 1rem 2rem;
      box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
      display: flex;
      justify-content: space-between;
      align-items: center;
    }

    .header-left {
      display: flex;
      align-items: center;
      gap: 1.5rem;
      flex-wrap: wrap;
    }

    h1 {
      margin: 0;
      font-size: 1.25rem;
      font-weight: 600;
      display: flex;
      align-items: center;
      gap: 0.5rem;
    }

    .main-nav {
      display: flex;
      align-items: center;
      gap: 0.25rem;
    }

    .main-nav a {
      color: rgba(255, 255, 255, 0.85);
      text-decoration: none;
      font-size: 0.875rem;
      font-weight: 500;
      padding: 0.35rem 0.75rem;
      border-radius: 6px;
      transition: background 0.2s, color 0.2s;
    }

    .main-nav a:hover {
      background: rgba(255, 255, 255, 0.08);
      color: #fff;
    }

    .main-nav a.active {
      background: rgba(255, 255, 255, 0.15);
      color: #fff;
    }

    .user-info {
      display: flex;
      align-items: center;
      gap: 1rem;
      font-size: 0.875rem;
    }
    
    .user-info code {
      background: rgba(255,255,255,0.1);
      padding: 0.25rem 0.5rem;
      border-radius: 4px;
    }

    .logout-btn {
      background: transparent;
      border: 1px solid rgba(255,255,255,0.3);
      color: white;
      padding: 0.4rem 0.8rem;
      border-radius: 4px;
      cursor: pointer;
      font-size: 0.875rem;
      transition: all 0.2s;
    }

    .logout-btn:hover {
      background: rgba(255,255,255,0.1);
    }

    main {
      padding: 2rem;
      max-width: 1400px;
      margin: 0 auto;
    }

    .card {
      background: white;
      border-radius: 8px;
      padding: 1.5rem;
      box-shadow: 0 1px 3px rgba(0,0,0,0.05);
      border: 1px solid #e1e4e8;
    }
  `;

  @state() private isLoggedIn = authStore.isLoggedIn;
  @state() private currentApiKey = authStore.apiKey;
  @state() private currentUsername = authStore.username;
  @state() private currentRole = authStore.role;

  /** 路由表：/ → 应用列表；/adapter → 协议适配器（History API，项目未安装 @lit-labs/router） */
  @state() private pathname: AppRoute = readAppRoute();

  // Modal 状态
  @state() private showModal = false;
  @state() private modalMode: 'create' | 'edit' = 'create';
  @state() private editingDevice: RobotInfo | null = null;

  private _authListener = () => {
    this.isLoggedIn = authStore.isLoggedIn;
    this.currentApiKey = authStore.apiKey;
    this.currentUsername = authStore.username;
    this.currentRole = authStore.role;
  };

  private _onPopState = () => {
    this.pathname = readAppRoute();
  };

  connectedCallback() {
    super.connectedCallback();
    authStore.addEventListener('auth-change', this._authListener);
    window.addEventListener('popstate', this._onPopState);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    authStore.removeEventListener('auth-change', this._authListener);
    window.removeEventListener('popstate', this._onPopState);
  }

  private navigate(path: AppRoute, e: Event) {
    e.preventDefault();
    if (this.pathname === path) return;
    history.pushState(null, '', path);
    this.pathname = path;
  }

  private handleLogout() {
    authStore.logout();
  }

  private handleOpenModal(e: CustomEvent) {
    this.modalMode = e.detail.mode;
    this.editingDevice = e.detail.device;
    this.showModal = true;
  }

  private handleModalClose() {
    this.showModal = false;
  }

  private handleDeviceSaved() {
    // 强制刷新 list：新增后回到第 1 页，避免新账号落在不可见页
    const list = this.shadowRoot?.querySelector('device-list-view') as
      | (HTMLElement & { refreshAfterSave?: () => Promise<void>; fetchDevices?: () => Promise<void> })
      | null
      | undefined;
    if (list?.refreshAfterSave) {
      void list.refreshAfterSave();
      return;
    }
    if (list?.fetchDevices) {
      void list.fetchDevices();
    }
  }

  render() {
    // 未登录时，全屏渲染登录框
    if (!this.isLoggedIn) {
      return html`<login-view></login-view>`;
    }

    // 成功登录后，渲染管理主界面
    return html`
      <header>
        <div class="header-left">
          <h1>统一接入与通信管理平台</h1>
          <nav class="main-nav" aria-label="主导航">
            <a
              href="/"
              class=${this.pathname === '/' ? 'active' : ''}
              @click=${(e: Event) => this.navigate('/', e)}
              >应用管理</a>
            <a
              href="/adapter"
              class=${this.pathname === '/adapter' ? 'active' : ''}
              @click=${(e: Event) => this.navigate('/adapter', e)}
              >协议适配器</a>
            <a
              href="/standard-model"
              class=${this.pathname === '/standard-model' ? 'active' : ''}
              @click=${(e: Event) => this.navigate('/standard-model', e)}
              >📋 标准模型管理</a>
            <a
              href="/http-target-system"
              class=${this.pathname === '/http-target-system' ? 'active' : ''}
              @click=${(e: Event) => this.navigate('/http-target-system', e)}
              >目标系统</a>
            <a
              href="/http-route-rule"
              class=${this.pathname === '/http-route-rule' ? 'active' : ''}
              @click=${(e: Event) => this.navigate('/http-route-rule', e)}
              >路由规则</a>
            <a
              href="/http-route-log"
              class=${this.pathname === '/http-route-log' ? 'active' : ''}
              @click=${(e: Event) => this.navigate('/http-route-log', e)}
              >路由日志</a>
            <a
              href="/http-route-debug"
              class=${this.pathname === '/http-route-debug' ? 'active' : ''}
              @click=${(e: Event) => this.navigate('/http-route-debug', e)}
              >路由调试</a>
          </nav>
        </div>
        <div class="user-info">
          <span>欢迎, <strong>${this.currentUsername}</strong> (${this.currentRole === 'admin' ? '管理员' : '租户'})</span>
          <span>标识: <code>${this.currentApiKey}</code></span>
          <button class="logout-btn" @click=${this.handleLogout}>退出登录</button>
        </div>
      </header>
      
      <main @open-device-modal=${this.handleOpenModal}>
        ${this.pathname === '/adapter'
          ? html`<protocol-adapter-view></protocol-adapter-view>`
          : this.pathname === '/standard-model'
            ? html`<standard-model-view></standard-model-view>`
            : this.pathname === '/http-target-system'
              ? html`<http-target-system-view></http-target-system-view>`
              : this.pathname === '/http-route-rule'
                ? html`<http-route-rule-view></http-route-rule-view>`
                : this.pathname === '/http-route-log'
                  ? html`<http-route-log-view></http-route-log-view>`
                  : this.pathname === '/http-route-debug'
                    ? html`<http-route-debug-view></http-route-debug-view>`
                    : html`<device-list-view></device-list-view>`}
      </main>

      <!-- 全局 Modal -->
      <device-form-modal
        ?open=${this.showModal}
        .mode=${this.modalMode}
        .device=${this.editingDevice}
        .currentApiKey=${this.currentApiKey || ''}
        @close=${this.handleModalClose}
        @device-saved=${this.handleDeviceSaved}
      ></device-form-modal>
    `;
  }
}
